import { LastCheckedProReview } from 'components/last-checked-pro-review';
import {
  useCountPresentationReviewByStatusQuery,
  usePresentationReviewStatsQuery,
} from 'generated/graphql';
import React, { FunctionComponent } from 'react';
import { Header, Icon, Label, List, Segment } from 'semantic-ui-react';
import styled from 'styled-components';
import { formatDate } from 'utils';
import { Review } from '../types/review.type';
import { ZoneButton } from './ZoneButton';

const MainTradeNames = styled.div`
  color: rgba(0, 0, 0, 0.6);
  font-style: italic;
  font-size: 12px;
  margin-top: 2px;
`;

type ReviewProSectionProps = {
  review: Review;
};

export const ReviewProSection: FunctionComponent<ReviewProSectionProps> = ({
  review,
}) => {
  const presentation = review.referenceView?.view?.proPresentation;

  const { data: countByStatusData, loading: countLoading } =
    useCountPresentationReviewByStatusQuery({
      variables: {
        where: {
          _and: [
            { proPresentationId: { _eq: presentation?.id } },
            { _or: [{ status: { _eq: 'draft' } }, { status: { _eq: 'ok' } }] },
          ],
        },
      },
    });

  const { data: reviewStatsData, loading: statsLoading } =
    usePresentationReviewStatsQuery({
      variables: {
        presentationId: presentation?.id ?? 0,
      },
      skip: !presentation,
    });

  const averageRating =
    reviewStatsData?.averageRating.aggregate?.avg?.rating ?? undefined;

  const latestReviewDate =
    reviewStatsData?.latestReview.aggregate?.max?.dateCreated ?? undefined;

  const okReviewCount =
    countByStatusData?.countProReviewByStatus?.find(
      (byStatus) => byStatus.status === 'ok',
    )?.count ?? 0;

  const draftReviewCount =
    countByStatusData?.countProReviewByStatus?.find(
      (byStatus) => byStatus.status === 'draft',
    )?.count ?? 0;

  if (!presentation) {
    return null;
  }

  return (
    <Segment.Group>
      <Segment>
        <Header as="h4" style={{ margin: 0 }}>
          Professionnel
        </Header>
      </Segment>
      <Segment>
        <List>
          <List.Item>
            <List.Content>
              <b>{presentation.name}</b>
              {presentation.mainTradeNames ? (
                <MainTradeNames>{presentation.mainTradeNames}</MainTradeNames>
              ) : null}
            </List.Content>
          </List.Item>
          {statsLoading || averageRating ? (
            <List.Item>
              <List.Content floated="right">
                {statsLoading ? (
                  <Label size="small" color="grey">
                    Chargement...
                  </Label>
                ) : (
                  <Label size="small" color="yellow">
                    <Icon name="star" />
                    {averageRating?.toFixed(1).replace('.', ',')}
                  </Label>
                )}
              </List.Content>
              <List.Content>Note moyenne</List.Content>
            </List.Item>
          ) : null}
          <List.Item>
            <List.Content floated="right">
              {countLoading ? (
                <Label size="small" color="grey">
                  Chargement...
                </Label>
              ) : (
                <>
                  <Label size="small" color="green">
                    {okReviewCount}
                  </Label>
                  {draftReviewCount > 0 ? (
                    <Label size="small" color="red">
                      {draftReviewCount}
                    </Label>
                  ) : null}
                </>
              )}
            </List.Content>
            <List.Content>Avis</List.Content>
          </List.Item>
          {statsLoading || latestReviewDate ? (
            <List.Item>
              <List.Content floated="right">
                <Label size="small" color="grey">
                  {latestReviewDate
                    ? formatDate(latestReviewDate)
                    : 'Chargement...'}
                </Label>
              </List.Content>
              <List.Content>Dernier avis reçu</List.Content>
            </List.Item>
          ) : null}
          <List.Item>
            <List.Content floated="right">
              <Label size="small" color="grey">
                <LastCheckedProReview proPresentationId={presentation.id} />
              </Label>
            </List.Content>
            <List.Content>Dernier avis vérifié</List.Content>
          </List.Item>
        </List>
        <div style={{ marginTop: '10px' }}>
          {presentation.proViews.map((proView) => {
            return (
              <ZoneButton
                key={`pro-zone-button-${proView.id}`}
                proView={proView}
                style={{ marginBottom: '6px' }}
              />
            );
          })}
        </div>
      </Segment>
    </Segment.Group>
  );
};
